import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

/**
 * Determines whether the current session should be blocked from calling the Post Office
 * Placement API.
 *
 * Context: a large share of Post Office Placement API traffic (and the resulting Stargate
 * rate-limit alerts) comes from Confluence synthetic "test tenants" (e.g.
 * `confluence-frontend-*-sev1-tests.atlassian.net`). Jira already suppresses Post Office
 * placements for non-Post-Office synthetic accounts; this mirrors that behaviour for
 * Confluence so those test tenants stop bombarding the Placement API.
 *
 * Logic:
 * - Only applies to synthetic tenants (detected via the existing
 *   `confluence_frontend_pollinator_synthetic_tenants` Statsig config, the same signal used
 *   to tag UFO `TenantType.Synthetic`).
 * - Post Office's own Pollinator accounts are allow-listed via the
 *   `post-office-is-post-office-synthetic-user` dynamic config so Post Office's own synthetic
 *   monitoring keeps working.
 *
 * @see https://switcheroo.atlassian.com/ui/configurations/c68cd47e-84b5-4fee-beb2-57d83566b16c/key/post-office-is-post-office-synthetic-user
 */
// eslint-disable-next-line confluence-feature-gating/inline-usage -- pre-existing exported wrapper, required by consumers
export const isRestrictedFromPostOfficeSynthetic = (): boolean => {
	const isSyntheticTenant = StatsigConfigurations.getBoolean(
		'confluence_frontend_pollinator_synthetic_tenants',
		false,
	);

	if (!isSyntheticTenant) {
		return false;
	}

	// Allow Post Office's own Pollinator accounts through (they rely on hitting the
	// Placement API for their own monitoring).
	return !StatsigConfigurations.getBoolean('post-office-is-post-office-synthetic-user', false);
};
